import { UrlConnectionEnum } from '../../enums/url-connection.enum';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { HttpService } from '../http-service/http.service';
import { SettingsService } from '../setting-service/setting.service';
export interface AlertSummary {
  checked: number;
  due: number;
  sent: number;
  retry: number;
  failed: number;
  review: number;
  suspended: number;
  skipped: number;
  reason?: string;
}
export interface AlertHistory {
  id: number;
  criacao: string;
  assunto: string;
  tipo: string;
  destinatarios: unknown;
}
export interface PendingAlert {
  id_consentimento: number;
  faixa: string;
  status: string;
  atualizado_em: string;
  tentativa_id?: string;
}
export interface AlertsDashboard {
  worker_ativo: boolean;
  worker_ultimo_check: string | null;
  enabled: boolean;
  hora_inicio: number;
  ultima_checagem_diaria_em: string | null;
  ultima_execucao: string | null;
  ultimo_resultado: AlertSummary | null;
  ultimo_erro: string | null;
  fuso: string;
  ambiente?: string;
  destinatarios_dev: string[];
  history: AlertHistory[];
  pending: PendingAlert[];
  modelos: { faixa: string; subject: string; text: string }[];
}
@Injectable({ providedIn: 'root' })
export class ConsentAlertsService {
  constructor(
    private http: HttpService,
    private settings: SettingsService,
  ) {}
  get ambiente(): 'dev' | 'prod' | null {
    const url = this.settings.configUso.urlApiPadrao || '';
    if (url.startsWith('https://api.pdag.chiesi.com.br/')) return 'prod';
    if (
      url.startsWith('https://api.dev.pdag.chiesi.com.br/') ||
      url.startsWith('http://localhost:3014/')
    )
      return 'dev';
    return null;
  }
  get enabled() {
    return this.ambiente !== null;
  }
  private dashboard(data: AlertsDashboard): AlertsDashboard {
    const ambiente = this.ambiente;
    if (!ambiente || (data.ambiente !== undefined && data.ambiente !== ambiente))
      throw Error('O ambiente retornado não corresponde à API. Recarregue antes de enviar.');
    return { ...data, ambiente };
  }
  private async request<T>(
    method: 'get' | 'post' | 'put',
    endpoint: UrlConnectionEnum,
    body: Record<string, unknown> = {},
  ): Promise<T> {
    const token = this.settings.user?.token;
    const apiUrl = this.settings.configUso.urlApiPadrao;
    if (!this.enabled || !token) throw Error('Entre novamente para administrar os alertas.');
    const response = await lastValueFrom(
      this.http[method](endpoint, body, this.http.setHeaderConfig(true)),
    );
    if (this.settings.user?.token !== token || this.settings.configUso.urlApiPadrao !== apiUrl)
      throw Error('A sessão mudou. Entre novamente.');
    if (response.status !== 200)
      throw Error(response.message || 'Não foi possível concluir a operação.');
    return response.result as T;
  }
  async get() {
    return this.dashboard(
      await this.request<AlertsDashboard>('get', UrlConnectionEnum.GET_CONSENT_ALERTS),
    );
  }
  async configure(body: { enabled: boolean }) {
    return this.dashboard(
      await this.request<AlertsDashboard>('put', UrlConnectionEnum.UPDATE_CONSENT_ALERTS, body),
    );
  }
  preview() {
    return this.request<AlertSummary>('post', UrlConnectionEnum.PREVIEW_CONSENT_ALERTS);
  }
  run(expectedEnvironment = this.ambiente) {
    if (!expectedEnvironment || expectedEnvironment !== this.ambiente)
      return Promise.reject(Error('O ambiente mudou. Recarregue antes de enviar.'));
    return this.request<AlertSummary>('post', UrlConnectionEnum.RUN_CONSENT_ALERTS, {
      confirmacao: expectedEnvironment === 'prod' ? 'ENVIAR_ALERTAS' : 'ENVIAR_ALERTAS_DEV',
    });
  }
  resolve(row: PendingAlert, action: 'confirmar_envio' | 'confirmar_nao_envio') {
    return this.request<AlertsDashboard>('post', UrlConnectionEnum.RESOLVE_CONSENT_ALERT, {
      id_consentimento: row.id_consentimento,
      faixa: row.faixa,
      tentativa_id: row.tentativa_id,
      atualizado_em: row.atualizado_em,
      acao: action,
      confirmacao: 'VERIFICADO_NO_PROVEDOR',
    });
  }
}
